import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  AlertTriangle,
  Sparkles,
  TrendingUp,
  Wrench,
  type LucideIcon,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";

export type ChangelogType = "feature" | "fix" | "breaking" | "improvement";
export type ChangelogAudience = "internal" | "external";

export interface ChangelogEntryRecord {
  id: string;
  title: string;
  description: string;
  type: ChangelogType;
  audience: ChangelogAudience;
  released_at: string;
  authored_by: string | null;
  created_at: string;
}

const TYPE_META: Record<
  ChangelogType,
  { label: string; icon: LucideIcon; pill: string; dot: string }
> = {
  feature: {
    label: "Nouveauté",
    icon: Sparkles,
    pill: "bg-fabrik-purple-soft text-fabrik-purple border-transparent",
    dot: "bg-fabrik-purple",
  },
  fix: {
    label: "Correctif",
    icon: Wrench,
    pill: "bg-fabrik-green-soft text-fabrik-accent-text border-transparent",
    dot: "bg-fabrik-green",
  },
  breaking: {
    label: "Breaking",
    icon: AlertTriangle,
    pill: "bg-fabrik-red-soft text-fabrik-red border-transparent",
    dot: "bg-fabrik-red",
  },
  improvement: {
    label: "Amélioration",
    icon: TrendingUp,
    pill: "bg-fabrik-blue-soft text-fabrik-blue border-transparent",
    dot: "bg-fabrik-blue",
  },
};

export const ChangelogEntry = ({ entry }: { entry: ChangelogEntryRecord }) => {
  const meta = TYPE_META[entry.type] ?? TYPE_META.feature;
  const Icon = meta.icon;
  // Parse explicitement à midi local pour éviter le décalage UTC qui
  // ramène "2026-05-11" à "2026-05-10" dans les fuseaux UTC-x.
  const dateLabel = format(
    new Date(`${entry.released_at}T12:00:00`),
    "d MMMM yyyy",
    { locale: fr },
  );

  return (
    <Card data-testid={`changelog-entry-${entry.type}`}>
      <CardContent className="py-4 md:py-5">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "size-9 shrink-0 rounded-lg flex items-center justify-center border",
              meta.pill,
            )}
          >
            <Icon className="size-4" />
          </div>

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <Badge
                variant="outline"
                className={cn("gap-1 text-[11px] font-medium", meta.pill)}
              >
                <span
                  className={cn("size-1.5 rounded-full", meta.dot)}
                  aria-hidden
                />
                {meta.label}
              </Badge>
              {entry.audience === "external" && (
                <Badge
                  variant="outline"
                  className="text-[11px] font-medium bg-fabrik-orange-soft text-fabrik-orange border-transparent"
                >
                  Public
                </Badge>
              )}
              <span className="text-[11.5px] text-muted-foreground">
                {dateLabel}
              </span>
            </div>

            <h3 className="text-[14px] font-semibold text-foreground leading-tight">
              {entry.title}
            </h3>
            <p className="text-[13px] text-foreground/85 mt-1.5 whitespace-pre-line">
              {entry.description}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
